import React, { createContext, useContext, useState } from "react";
import {
  addTransactionItemApi,
  createTransactionApi,
  deleteTransactionItemApi,
  updateTransactionItemApi,
  useGetActiveCartTransaction,
} from "src/api/transaction";
import { IReservation } from "src/types/reservation";
import {
  IFullTransactionCreate,
  ITransaction,
  ITransactionItem,
} from "src/types/transaction";
import { endpoints } from "src/utils/axios";

export type ITransactionContext = {
  activeTransaction: Partial<ITransaction>;
  transactionItems: ITransactionItem[];
  needToRefreshCart: boolean;
  //   clickedEventAdded: boolean;
  createTransaction: (transaction: Partial<ITransaction>) => Promise<any>;
  addTransactionItem: (item: any) => Promise<ITransactionItem[]>;
  updateTransactionItem: (itemId: number, updates: any) => Promise<any>;
  setActiveTransaction: (transaction: Partial<ITransaction>) => void;
  setTransactionItems: (items: (ITransactionItem & IReservation)[]) => void;
  deleteTransactionItem: (id: number) => Promise<number>;
  setNeedToRefreshCart: (bool: boolean) => void;
  //   setClickedEventAdded: (bool: boolean) => void;
};

const TransactionContext = createContext<ITransactionContext | undefined>(
  undefined
);

export const TransactionProvider = ({ children }: any) => {
  const [activeTransaction, setActiveTransaction] = useState<
    Partial<ITransaction>
  >({});
  const [needToRefreshCart, setNeedToRefreshCart] = useState<boolean>(false);

  const [transactionItems, setTransactionItems] = useState<
    (ITransactionItem & IReservation)[]
  >([]);

  const createTransaction = async (transaction: Partial<ITransaction>) => {
    try {
      const res = await createTransactionApi(transaction);

      if (res.status && res.status === 200 && res.data.transaction) {
        setActiveTransaction(res.data.transaction);
        setNeedToRefreshCart(true);
        return res.data.transaction;
      } else {
        console.error("Failed to create transaction:", res.statusText);
        return null;
      }
    } catch (error) {
      console.error("Error creating transaction: ", error);
      throw error;
    }
  };

  const addTransactionItem = async (item: IFullTransactionCreate) => {
    try {
      // Call the API to add the transaction item
      const response = await addTransactionItemApi(item);

      // Check if the response contains the expected data
      if (!response || !response.data || !response.data.added_items) {
        throw new Error("Invalid response from API");
      }

      // Extract the added items from the response
      const newItems: any[] = response.data.added_items;

      if (!Array.isArray(newItems) || newItems.length === 0) {
        throw new Error("No items were added");
      }

      // Update the state with the newly added items
      setTransactionItems((prev) => [...prev, ...newItems]);

      setNeedToRefreshCart(true);
      // Return the newly added items
      return newItems;
    } catch (error) {
      // Handle errors gracefully
      console.error("Failed to add transaction item:", error);
      if (error.detail === "transaction_not_found") {
        //window.location.reload();
        setActiveTransaction({});
      }
      // Rethrow the error if needed or return a meaningful fallback
      throw error;
    }
  };

  const deleteTransactionItem = async (transactionItemId: number) => {
    try {
      const res = await deleteTransactionItemApi(transactionItemId);

      if (res.status === 204) {
        setTransactionItems((prev) => {
          const updatedItems = prev.filter(
            (item) => item.id !== transactionItemId
          );

          if (updatedItems.length === 0) {
            setActiveTransaction({});
          }
          setNeedToRefreshCart(true);
          return updatedItems; // Return the updated state
        });
        return res.status;
      } else {
        console.error("Failed to delete transaction item:", res.statusText);
        return res.status;
      }
    } catch (error) {
      console.error("Error deleting transaction item:", error);
      return 0;
    }
  };

  const updateTransactionItem = async (itemId: number, updates: any) => {
    try {
      const res = itemId
        ? await updateTransactionItemApi(itemId, updates)
        : null;

      if (res?.status === 200) {
        // Extract updated reservation data from response
        const updatedReservation = res.data?.reservation;
        const updatedTransactionItem = res.data?.transaction_item;
        let newUpdate = {};

        if (updates.startTime) {
          newUpdate = {
            start_date: updatedReservation.start_date,
            price: updatedTransactionItem.price,
            credit: updatedTransactionItem.credit,
          };
        } else if (updates.endTime) {
          newUpdate = {
            end_date: updatedReservation.end_date,
            price: updatedTransactionItem.price,
            credit: updatedTransactionItem.credit,
          };
        }

        // Update the state with the new values
        setTransactionItems((prev) =>
          prev.map((item: any) =>
            item.id === itemId
              ? {
                  ...item,
                  ...newUpdate,

                  reservation: {
                    ...item.reservation,
                    ...newUpdate,
                  },
                  //...updates, // Include other updates from input
                }
              : item
          )
        );

        // Trigger cart refresh if needed
        setNeedToRefreshCart(true);
        return res;
      } else {
        console.error("Failed to update transaction item:", res?.statusText);
        return res;
      }
    } catch (error) {
      console.error("Error updating transaction item:", error);
      return error;
    }
  };

  return (
    <TransactionContext.Provider
      value={{
        activeTransaction,
        transactionItems,
        needToRefreshCart,
        // clickedEventAdded,
        createTransaction,
        addTransactionItem,
        updateTransactionItem,
        setActiveTransaction,
        setTransactionItems,
        deleteTransactionItem,
        setNeedToRefreshCart,
        // setClickedEventAdded,
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};

export const useTransaction = (): ITransactionContext => {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error("useTransaction must be used within a TransactionProvider");
  }
  return context;
};
